import { isReservedBookingSlug } from '@arrival/shared/validation/bookingSlug'

/**
 * Landing page of booking-only accounts.
 * `/my-bookings` works on both provider and booking hosts; `/settings` is marketing-gated on booking.
 */
export const GUEST_ONLY_HOME_PATH = '/my-bookings'

/** Provider-app pages a booking-only account may open: bookings + account hub (provider host). */
const GUEST_ONLY_ROUTE_PATHS = new Set<string>([GUEST_ONLY_HOME_PATH, '/settings'])

export function isGuestOnlyAllowedRoute(path: string): boolean {
  return GUEST_ONLY_ROUTE_PATHS.has(path) || guestBookingDetailAppointmentId(path) !== null
}

/** Appointment id of a guest booking manage page (`/my-bookings/:id`), else null. */
export function guestBookingDetailAppointmentId(path: string): string | null {
  return path.match(/^\/my-bookings\/([^/]+)\/?$/)?.[1] ?? null
}

/** Guest booking manage list/detail — allowed on both provider and booking hosts. */
export function isGuestBookingManageRoute(path: string): boolean {
  return path === '/my-bookings' || path === '/my-bookings/' || guestBookingDetailAppointmentId(path) !== null
}

/** `redirect_url` query value when it is an in-app path (no external or protocol-relative URLs). */
export function safeInternalRedirectPath(raw: unknown): string | null {
  return typeof raw === 'string' && raw.startsWith('/') && !raw.startsWith('//') && !raw.includes('\\') ? raw : null
}

/** Routes reachable without a Clerk session (sign-in / sign-up flows). */
export const PUBLIC_ROUTE_PATHS = new Set<string>([
  '/login',
  '/registration',
  '/registration-provider',
  '/registration-client',
  '/registration/type',
  '/verification-code',
  '/sitemap',
])

export function isPublicRoute(path: string): boolean {
  return PUBLIC_ROUTE_PATHS.has(path)
}

export function bookingHandleFromPath(path: string): string | null {
  const match = path.match(/^\/([^/]+)(?:\/|$)/)
  if (!match) {
    return null
  }
  const handle = match[1]
  if (!handle || isReservedBookingSlug(handle)) {
    return null
  }
  return handle
}

function bookingSuffix(path: string): string | null {
  const handle = bookingHandleFromPath(path)
  if (!handle) {
    return null
  }
  return path.slice(handle.length + 1) || '/'
}

/** Calendar + summary require a complete guest identity (email + phone + name). */
export function isBookingIdentityRequiredRoute(path: string): boolean {
  const suffix = bookingSuffix(path)
  if (suffix === null) {
    return false
  }
  return suffix.includes('/calendar') || suffix === '/summary' || suffix.endsWith('/summary')
}

/** Signed-in guest account pages — not reachable without a Clerk session. */
export function isBookingSignedInRequiredRoute(path: string): boolean {
  const suffix = bookingSuffix(path)
  if (suffix === null) {
    return false
  }
  return (
    suffix === '/account' ||
    suffix.startsWith('/account/') ||
    suffix === '/appointments' ||
    suffix.startsWith('/appointments/')
  )
}

/** Guest booking until sign-in: catalog, service, identify, magic-link verify.
 * Calendar/summary need Clerk or a complete local guest identity (see route guard). */
export function isBookingPublicRoute(path: string): boolean {
  if (!isBookingRoute(path)) {
    return false
  }
  return !isBookingIdentityRequiredRoute(path) && !isBookingSignedInRequiredRoute(path)
}

export function isBookingRoute(path: string): boolean {
  return bookingHandleFromPath(path) !== null
}

/**
 * Provider-app world allowlist (settings, auth, calendar, my-bookings, …).
 * Opposite partition of `isBookingRoute` — app host gate uses only this, never booking checks.
 */
export function isProviderAppRoute(path: string): boolean {
  return !isBookingRoute(path)
}

/** True when the shell may render this path without a Clerk session. */
export function isSessionOptionalRoute(path: string): boolean {
  return isPublicRoute(path) || isBookingPublicRoute(path)
}
